import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { Card, message, Skeleton } from 'antd'
import { useStyles } from '../../style'
import GeneratedImage from '../GeneratedImage'
import { useConversationContext } from '../../../../stores/conversation.store'
import { getImage } from '../../../../api/image'
import RequestBubble from '../../../components/RequestBubble'
import ResponseBubble from '../../../components/ResponseBubble'
import type { ExtendedChatMessage } from '../../types'
import { scrollToBottom } from '../../../../utils'
import ChatInput from '../../../ChatPage/components/input'
import BasePage from '../../../components/BasePage'
import ImageDetailDrawer from '../ImageDetailDrawer'
import { imageConfigAtom, configList } from '../../../../stores/imageAtom.tsx'
import { atom, useAtom } from 'jotai'
const ImageGenConversationView: React.FC<{ conversationId: string }> = ({
  conversationId,
}) => {
  const { styles } = useStyles()
  const location = useLocation()
  const [config, setConfig] = useAtom(imageConfigAtom)
  const [inputContent, setInputContent] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [drawerData, setDrawerData] = useState({})
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const {
    activeConversation,
    chooseActiveConversation,
    updateActiveConversation,
    fetchConversations,
  } = useConversationContext()

  const isFirstLoad = useRef(true)
  const processedPrompts = useRef(new Set<string>())

  useEffect(() => {
    chooseActiveConversation(conversationId)
  }, [conversationId])

  // 监听消息变化，触发滚动
  useEffect(() => {
    scrollToBottom(messagesContainerRef.current)
  }, [activeConversation?.messages])

  useEffect(() => {
    if (isFirstLoad.current && activeConversation) {
      const queryParams = new URLSearchParams(location.search)
      const urlPrompt = queryParams.get('prompt')

      if (urlPrompt && !processedPrompts.current.has(urlPrompt)) {
        // 标记此prompt已处理，避免重复生成
        processedPrompts.current.add(urlPrompt)

        // 清除URL中的prompt参数，防止刷新页面重复发送
        const newUrl = window.location.hash.split('?')[0]
        window.history.replaceState({}, document.title, newUrl)

        handleGenerateImage(urlPrompt)
        const timeId = setTimeout(() => {
          fetchConversations()
          clearTimeout(timeId)
        }, 3000)
      }

      isFirstLoad.current = false
    }
  }, [location.search, activeConversation])

  const handleGenerateImage = async (prompt: string) => {
    if (!prompt.trim() || !activeConversation) return

    setIsGenerating(true)
    setInputContent('')
    // 通用：替换占位消息

    const startTimeoutCheck = (
      updatedMessages: ExtendedChatMessage[],
      imageMessage: ExtendedChatMessage
    ) => {
      return window.setTimeout(() => {
        if (isGenerating) {
          setIsGenerating(false)
          handleReplace(updatedMessages, imageMessage, '图像生成超时，请重试')
        }
      }, 30000)
    }
    const handleReplace = (
      nowMessages: ExtendedChatMessage[],
      newMessage: ExtendedChatMessage,
      errorText?: string,
      response?: Response
    ) => {
      const replaceMessages =
        errorText == ''
          ? nowMessages.map((msg) =>
              msg.messageId === newMessage.messageId
                ? ({
                    ...msg,
                    ...response,
                    isFinished: true,
                  } as ExtendedChatMessage)
                : msg
            )
          : nowMessages.map((msg) =>
              msg.messageId === newMessage.messageId
                ? ({
                    ...msg,
                    content: errorText,
                    isError: true,
                    isFinished: true,
                  } as ExtendedChatMessage)
                : msg
            )

      updateActiveConversation({
        ...activeConversation,
        messages: replaceMessages,
      })

      if (errorText != '') message.error(errorText)
    }

    const timestamp = String(Date.now())
    const userMessage: ExtendedChatMessage = {
      role: 'USER',
      content: prompt.trim(),
      createTime: timestamp,
      messageId: timestamp,
      isError: false,
    }
    const imageMessage: ExtendedChatMessage = {
      role: 'ASSISTANT',
      content: '',
      messageId: String(Date.now() + 1),
      createTime: String(Date.now() + 1),
      isError: false,
      isFinished: false,
    }

    const updatedMessages = [
      ...activeConversation.messages,
      userMessage,
      imageMessage,
    ]
    updateActiveConversation({
      ...activeConversation,
      messages: updatedMessages,
    })
    scrollToBottom(messagesContainerRef.current)

    let timeoutId: number | null = null

    try {
      // 超时检测
      timeoutId = startTimeoutCheck(updatedMessages, imageMessage)

      const response = await getImage(prompt.trim() + config.style, {
        conversationId,
        genNum: config.count,
        size: config.ratio,
      })
      if (timeoutId) clearTimeout(timeoutId)

      if (response) {
        handleReplace(updatedMessages, imageMessage, '', response.data)
      } else throw new Error('图像返回异常')
    } catch (error) {
      console.error('处理图像生成请求错误:', error)
      if (timeoutId) clearTimeout(timeoutId)
      handleReplace(updatedMessages, imageMessage, '图像生成失败，请重试')
    } finally {
      setIsGenerating(false)
    }
  }

  // 重新生成图像
  const handleRegenerate = async (prompt: string, messageId: string) => {
    if (!activeConversation) return

    setIsGenerating(true)
    const updatedMessages = activeConversation.messages.map((message) =>
      message.messageId === messageId
        ? {
            ...message,
            isFinished: false,
          }
        : message
    )
    updateActiveConversation({
      ...activeConversation,
      messages: updatedMessages,
    })
    try {
      message.info('正在重新绘制中，请稍等')
      // 调用图像生成API
      const newImage = await getImage(prompt + config.style, {
        conversationId: activeConversation.conversationId,
        genNum: config.count,
        size: config.ratio,
      })

      if (newImage) {
        // 更新消息中的图像
        const updatedMessages = activeConversation.messages.map((message) =>
          message.messageId === messageId
            ? {
                ...message,
                ...newImage.data,
                isFinished: true,
              }
            : message
        )

        // 更新会话
        updateActiveConversation({
          ...activeConversation,
          messages: updatedMessages,
        })
      } else {
        message.error('图像重新生成失败')
      }
    } catch (error) {
      console.error('重新生成图像错误:', error)
      message.error('图像重新生成失败，请重试')
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <BasePage title="AI 图像生成">
      <div className={styles.container}>
        {/* 消息和生成的图片展示区域 */}
        <div className={styles.messagesContainer}>
          <div className={styles.article} ref={messagesContainerRef}>
            {activeConversation?.messages.map((message: any) => (
              <div key={message.messageId} className={styles.messageItem}>
                {/* 用户消息 */}
                {message.role === 'USER' && (
                  <RequestBubble content={message.content} />
                )}

                {/* 生成的图像 */}
                {message.role === 'ASSISTANT' && (
                  <div className={styles.imageGallery}>
                    {message.isFinished && (
                      <ResponseBubble
                        content={message.content}
                        isError={message.isError}
                        isFinished={message.isFinished}
                      />
                    )}
                    <div
                      style={{
                        padding: '0 20px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: 10,
                      }}
                    >
                      {!message.isFinished ? (
                        <Skeleton.Image
                          active
                          style={{ width: 350, height: 350, borderRadius: 8 }}
                        />
                      ) : (
                        message.medium?.images.map((image: any) => (
                          <Card
                            key={image.url}
                            styles={{ body: { padding: 0 } }}
                            className={styles.imageCard}
                          >
                            <GeneratedImage
                              id={image.url}
                              url={image.url}
                              prompt={image.prompt}
                              setVisible={() => {
                                setDrawerVisible(true)
                                setDrawerData(message)
                              }}
                              onReload={(prompt) =>
                                handleRegenerate(prompt, message.messageId)
                              }
                            />
                          </Card>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <ChatInput
          inputValue={inputContent}
          onInputChange={setInputContent}
          onSubmit={handleGenerateImage}
          isLoading={isGenerating}
          isImage={true}
          placeholder="您可以描述您所想象的画面，角色，情绪，场景，风格..."
        />
        <ImageDetailDrawer
          show={drawerVisible}
          data={drawerData}
          onClose={() => setDrawerVisible(false)}
        />
      </div>
    </BasePage>
  )
}

export default ImageGenConversationView
